<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="text-h4 mb-6">Mon rédacteur IA - Créer un POST</h1>
      </v-col>
    </v-row>

    <!-- Site Selection - Must be done first -->
    <v-row v-if="!localSiteId">
      <v-col cols="12" md="8" offset-md="2">
        <v-card class="mb-6">
          <v-card-title>
            <span class="text-h5">Sélection du site</span>
          </v-card-title>
          <v-card-text>
            <div>Current site ID: {{ localSiteId }}</div>
            <v-alert type="info" outlined class="mb-4" v-if="!isLoadingSites">
              Veuillez sélectionner un site pour continuer. Cette sélection sera
              utilisée pour la recherche de sources et la publication.
            </v-alert>
            <v-alert type="info" outlined class="mb-4" v-else>
              Chargement de la liste des sites...
            </v-alert>

            <v-autocomplete
              v-model="selectedSite"
              :items="siteItems"
              :loading="isLoadingSites"
              :disabled="isLoadingSites || siteItems.length === 0"
              item-text="site.name"
              item-value="site.id"
              label="Sites"
              :placeholder="
                siteItems.length === 0
                  ? 'Aucun site disponible'
                  : 'Sélectionnez un site'
              "
              outlined
              dense
              hide-no-data
              clearable
              return-object
            >
              <template v-slot:item="{ item }">
                <v-list-item-avatar color="grey lighten-2">
                  <v-icon fab color="blue-grey darken-2">{{
                    icons.mdiWordpress
                  }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title
                    v-text="
                      item && item.site && item.site.name
                        ? item.site.name
                        : 'Site inconnu'
                    "
                  ></v-list-item-title>
                  <v-list-item-subtitle
                    v-text="item && item.site_url ? item.site_url : ''"
                  ></v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main content - Only shown after site selection -->
    <div v-else>
      <div>Showing main content for site ID: {{ localSiteId }}</div>
      <TopicSourcesForm :site-id="localSiteId" />

      <WordPressPublisher v-if="sources && sources.length > 0" :site-id="localSiteId" :sites="siteItems" @update:siteId="handleSiteIdUpdate" />
    </div>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import TopicSourcesForm from "@/components/ai-factory/generate-post/TopicSourcesForm.vue";
import WordPressPublisher from "@/components/ai-factory/generate-post/SendToPostGenerationQueue.vue";
import { mdiWordpress } from "@mdi/js";
import axios from "@axios";

export default {
  name: "AIFactoryGeneratePost",

  components: {
    TopicSourcesForm,
    WordPressPublisher,
  },

  data() {
    return {
      isScrapingLoading: false,
      isLoadingSites: false,
      siteItems: [],
      localSiteId: null, // Local site ID for reliable reactivity
      isLoadingItemData: false, // Ajout pour le chargement des données de l'item
      icons: {
        mdiWordpress,
      },
    };
  },

  computed: {
    ...mapGetters("aiFactoryTmp", [
      "getTopic",
      "getSources",
      "getScrapedSources",
      "isLoading",
      "getError",
    ]),

    topic: {
      get() {
        return this.getTopic;
      },
      set(value) {
        this.updateTopic(value);
      },
    },

    sources: {
      get() {
        return this.getSources.length > 0 ? this.getSources : [""];
      },
      set(value) {
        this.updateSources(value);
      },
    },

    selectedSite: {
      get() {
        // Find the full site object from the siteItems based on the stored site ID
        if (
          !this.localSiteId ||
          !Array.isArray(this.siteItems) ||
          this.siteItems.length === 0
        )
          return null;
        return (
          this.siteItems.find(
            (item) => item && item.site && item.site.id === this.localSiteId
          ) || null
        );
      },
      set(value) {
        // Store only the site.id in both Vuex store and local data
        const siteId = value && value.site ? value.site.id : null;
        console.log("Site selected, storing ID:", siteId);
        this.updateSelectedSite(siteId); // Update Vuex store
        this.localSiteId = siteId; // Update local data for reliable reactivity
      },
    },

    scrapedSources() {
      return this.getScrapedSources;
    },

    ...mapGetters("aiFactoryTmp", ["getSites"]),

    sites() {
      return this.getSites;
    },

    hasSitesAvailable() {
      return Array.isArray(this.siteItems) && this.siteItems.length > 0;
    },
  },

  watch: {
    localSiteId(newVal, oldVal) {
      if (newVal !== oldVal) {
        console.log("Local site ID changed:", newVal);
      }
    },
  },

  async mounted() {
    await this.loadAllSites();
    await this.loadItemData();
  },

  methods: {
    ...mapActions("aiFactoryTmp", [
      "fetchConnectors",
      "scrapeSources",
      "testPrompt",
      "updateTopic",
      "updateSources",
      "addSource",
      "removeSource",
      "updateSelectedSite",
      "updateModelParams",
    ]),

    addSourceField() {
      this.addSource("");
    },

    removeSourceField(index) {
      this.removeSource(index);
    },

    async loadAllSites() {
      this.isLoadingSites = true;
      try {
        const response = await this.fetchConnectors();
        this.siteItems = response.items || [];

        // Show warning if no sites are available
        if (!this.hasSitesAvailable) {
          await this.$store.dispatch("app/setSnackBar", {
            show: true,
            text: `Aucun site disponible pour la sélection.`,
            color: "warning",
            timeout: 4000,
          });
        }
      } catch (error) {
        console.error("Erreur lors du chargement des sites:", error);
        this.siteItems = [];
        await this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: `Erreur lors du chargement de la liste des sites: ${error.message}`,
          color: "error",
          timeout: 4000,
        });
      } finally {
        this.isLoadingSites = false;
      }
    },

    async loadItemData() {
      const itemId = this.$route.params.id;
      if (!itemId) return;

      this.isLoadingItemData = true;
      try {
        const response = await axios.get(`/api/ai-factory/get-item`, {
          params: { id: itemId },
        });
        const itemData = response.data;

        if (itemData && itemData.site_id) {
          // Pré-sélectionner le site si présent dans les données de l'item
          this.localSiteId = itemData.site_id;
          this.updateSelectedSite(itemData.site_id);

          await this.$store.dispatch("app/setSnackBar", {
            show: true,
            text: `Données de la demande #${itemId} chargées avec succès.`,
            color: "success",
            timeout: 3000,
          });
        } else {
          // Si aucun site n'est présent dans les données de l'item,
          // vérifier si un site est déjà sélectionné dans le sélecteur principal
          if (!this.localSiteId && this.siteItems.length > 0) {
            // Sélectionner le premier site par défaut
            this.localSiteId = this.siteItems[0].site.id;
            this.updateSelectedSite(this.localSiteId);
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement des données de l'item:", error);
        await this.$store.dispatch("app/setSnackBar", {
          show: true,
          text: `Erreur lors du chargement des données de la demande: ${error.message}`,
          color: "error",
          timeout: 4000,
        });
      } finally {
        this.isLoadingItemData = false;
      }
    },

    async handleScrapeSources() {
      this.isScrapingLoading = true;
      try {
        await this.$store.dispatch("aiFactory/scrapeSources");
        this.$toast?.success("Sources scraped successfully") ||
          console.log("Sources scraped");
      } catch (error) {
        this.$toast?.error("Error scraping sources: " + error.message) ||
          console.error(error);
      } finally {
        this.isScrapingLoading = false;
      }
    },

    handleSiteIdUpdate(newSiteId) {
      this.localSiteId = newSiteId;
      this.updateSelectedSite(newSiteId);
    },
  },
};
</script>

<style scoped>
.v-tabs {
  margin-bottom: 24px;
}
</style>
